import { Link } from 'react-router-dom'
import {
  MarketingShell,
  Seo,
  Container,
  Section,
  PageHero,
  LegalLayout,
  Prose,
  Reveal,
} from './kit.jsx'

const TOC = [
  { id: 'parties', label: '1. Parties & roles' },
  { id: 'scope', label: '2. Scope & subject matter' },
  { id: 'data', label: '3. Data & data subjects' },
  { id: 'obligations', label: '4. Processor obligations' },
  { id: 'confidentiality', label: '5. Confidentiality' },
  { id: 'security', label: '6. Security measures' },
  { id: 'subprocessors', label: '7. Subprocessors' },
  { id: 'transfers', label: '8. International transfers' },
  { id: 'requests', label: '9. Data subject requests' },
  { id: 'breach', label: '10. Breach notification' },
  { id: 'audit', label: '11. Audit rights' },
  { id: 'deletion', label: '12. Deletion & return' },
  { id: 'signatures', label: '13. Signatures & contact' },
]

export default function DpaPage() {
  return (
    <MarketingShell active="">
      <Seo
        title="Data Processing Addendum"
        description="The Revailo Sales Data Processing Addendum — how we process business contacts and customer end-user data as a processor, our security measures, subprocessors, and data-subject commitments."
        path="/dpa"
        keywords="Revailo Sales DPA, data processing addendum, GDPR processor, b2b data privacy, subprocessors"
      />

      <PageHero
        eyebrow="Legal"
        title="Data Processing Addendum"
        lead="This addendum governs how Revailo Sales processes personal data on behalf of the businesses who use our platform. It forms part of, and is incorporated into, our Terms of Service."
        align="center"
      />

      <Section sm>
        <Container>
          <Reveal>
            <LegalLayout toc={TOC}>
              <Prose>
                <p className="text-[15px] text-dim">Last updated: July 2026</p>
                <p>
                  This Data Processing Addendum (the &ldquo;DPA&rdquo;) applies whenever Revailo
                  Sales processes personal data on behalf of a customer in connection with the
                  Revailo Sales service &mdash; the lead engine that gives B2B sales
                  businesses access to 10,942 verified companies, multi-step
                  email outreach, deal-flow pipeline, and analytics. Where this DPA conflicts with
                  the Terms of Service on the subject of data protection, this DPA controls.
                </p>

                <h2 id="parties">1. Parties &amp; roles</h2>
                <p>
                  For personal data processed through the service, the customer (the contracting
                  business operating a Revailo Sales workspace) acts as the <strong>controller</strong>
                  and determines the purposes and means of processing. Revailo Sales acts as the
                  <strong> processor</strong>, processing that data only on the customer&rsquo;s
                  documented instructions.
                </p>
                <p>
                  Two data flows fall under this DPA. First, the verified company records in
                  the platform directory &mdash; business contacts the customer
                  selects, filters, and works. Second, the customer&rsquo;s own end-user data &mdash;
                  the teammates a customer invites into a workspace, and any prospect who replies to
                  outreach. For its own account administration and billing, Revailo Sales acts as an
                  independent controller as described in our{' '}
                  <Link to="/privacy">Privacy Notice</Link>.
                </p>

                <h2 id="scope">2. Scope &amp; subject matter</h2>
                <p>
                  The subject matter of processing is the provision of the Revailo Sales service. The
                  duration matches the term of the customer&rsquo;s subscription plus the retention
                  and deletion windows described in Section 12. The nature and purpose of processing
                  is to store, organize, filter, and enrich lead records; to send and
                  receive multi-step outreach through the customer&rsquo;s own connected Gmail
                  account; to maintain a deal-flow pipeline and activity history; and to surface
                  analytics and in-app assistant answers derived from the workspace&rsquo;s own data.
                </p>

                <h2 id="data">3. Categories of data &amp; data subjects</h2>
                <p>
                  Revailo Sales processes the following categories of personal data on the
                  customer&rsquo;s behalf:
                </p>
                <ul>
                  <li>
                    <strong>Business contacts</strong> &mdash; company name, industry
                    classification, region and service area, established year, company size, annual
                    volume, and named business contacts (name, business email, business phone) drawn
                    from the 10,942 companies across the verticals you sell into in the dataset.
                  </li>
                  <li>
                    <strong>Customer end users</strong> &mdash; the workspace owner and invited
                    teammates: name, email, role, and authentication identifiers managed through
                    Amazon Cognito.
                  </li>
                  <li>
                    <strong>Outreach correspondents</strong> &mdash; the content and metadata of
                    messages a prospect sends in reply to a sequence, logged against the relevant
                    account timeline.
                  </li>
                </ul>
                <p>
                  The data subjects are business contacts at companies, the
                  customer&rsquo;s own personnel, and prospects who engage with the customer&rsquo;s
                  outreach. Revailo Sales does not intentionally collect special categories of
                  personal data, and customers must not use the service to do so.
                </p>

                <h2 id="obligations">4. Processor obligations</h2>
                <p>Revailo Sales will:</p>
                <ul>
                  <li>
                    process personal data only on the customer&rsquo;s documented instructions,
                    including the instructions embodied in the customer&rsquo;s configuration of the
                    service, unless required to act otherwise by law;
                  </li>
                  <li>
                    not sell customer personal data and not use it for any purpose other than
                    providing and improving the service the customer has purchased;
                  </li>
                  <li>
                    make available the information reasonably necessary to demonstrate compliance
                    with this DPA and assist the customer in meeting its own obligations, including
                    responding to data-subject requests and carrying out impact assessments;
                  </li>
                  <li>
                    promptly inform the customer if, in our opinion, an instruction infringes
                    applicable data-protection law.
                  </li>
                </ul>

                <h2 id="confidentiality">5. Confidentiality</h2>
                <p>
                  Revailo Sales ensures that personnel authorized to process customer personal data
                  are bound by written confidentiality commitments, access data strictly on a
                  need-to-know basis, and receive appropriate data-protection and security training.
                  Confidentiality obligations survive the end of employment or engagement.
                </p>

                <h2 id="security">6. Security measures</h2>
                <p>
                  Revailo Sales maintains technical and organizational measures appropriate to the
                  risk, including:
                </p>
                <ul>
                  <li>encryption of data in transit (TLS) and at rest;</li>
                  <li>
                    authentication and access control through Amazon Cognito, with role-based
                    permissions and support for strong passwords;
                  </li>
                  <li>
                    OAuth-scoped Gmail connections &mdash; outreach is sent through the
                    customer&rsquo;s own account and Revailo Sales never stores the customer&rsquo;s
                    Google password;
                  </li>
                  <li>
                    network isolation, least-privilege service roles, and audit logging across the
                    hosting environment;
                  </li>
                  <li>regular backups and documented restore procedures.</li>
                </ul>

                <h2 id="subprocessors">7. Subprocessors</h2>
                <p>
                  The customer authorizes Revailo Sales to engage the subprocessors below to deliver
                  the service. Each is bound by data-protection terms no less protective than this
                  DPA.
                </p>
                <ul>
                  <li>
                    <strong>Amazon Web Services (AWS)</strong> &mdash; cloud hosting, database
                    storage (DynamoDB), compute (Lambda), and Cognito authentication.
                  </li>
                  <li>
                    <strong>Google LLC</strong> &mdash; Gmail API for sending and receiving the
                    customer&rsquo;s own outreach.
                  </li>
                </ul>
                <p>
                  Revailo Sales will give the customer prior notice of any intended addition or
                  replacement of a subprocessor, allowing a reasonable opportunity to object on
                  legitimate data-protection grounds.
                </p>

                <h2 id="transfers">8. International transfers</h2>
                <p>
                  Personal data is primarily hosted in AWS regions within the United States. Where
                  providing the service requires transferring personal data across borders, Revailo
                  Pro relies on an appropriate transfer mechanism &mdash; such as the applicable
                  Standard Contractual Clauses &mdash; and applies supplementary measures where
                  needed to ensure a level of protection consistent with the origin jurisdiction.
                </p>

                <h2 id="requests">9. Data subject requests</h2>
                <p>
                  Taking into account the nature of the processing, Revailo Sales assists the customer
                  by appropriate technical and organizational measures, insofar as possible, in
                  fulfilling the customer&rsquo;s obligation to respond to requests to exercise data
                  subject rights &mdash; access, rectification, erasure, restriction, portability,
                  and objection. If Revailo Sales receives such a request directly, we will promptly
                  forward it to the customer and will not respond independently except as legally
                  required or as instructed by the customer.
                </p>

                <h2 id="breach">10. Breach notification</h2>
                <p>
                  Revailo Sales will notify the customer without undue delay, and in any event within
                  72 hours, after becoming aware of a personal data breach affecting the
                  customer&rsquo;s data. The notice will describe the nature of the breach, the
                  categories and approximate number of data subjects and records concerned, the
                  likely consequences, and the measures taken or proposed to address it. Revailo Sales
                  will cooperate with the customer&rsquo;s own breach-reporting obligations.
                </p>

                <h2 id="audit">11. Audit rights</h2>
                <p>
                  Revailo Sales makes available to the customer the information necessary to
                  demonstrate compliance with this DPA and allows for and contributes to audits,
                  including inspections, conducted by the customer or an auditor it mandates. Audits
                  are conducted no more than once per twelve-month period unless required by a
                  supervisory authority, on reasonable prior notice, during business hours, and
                  subject to confidentiality obligations that protect other customers&rsquo; data.
                </p>

                <h2 id="deletion">12. Deletion &amp; return on termination</h2>
                <p>
                  On termination or expiry of the subscription, and at the customer&rsquo;s choice,
                  Revailo Sales will return or delete the customer&rsquo;s personal data. Unless the
                  customer requests export first, workspace data is deleted within 30 days of
                  termination, after which residual copies are removed from backups within their
                  normal rotation cycle, except where retention is required by law. The shared,
                  read-only source dataset that seeds the platform is not customer personal
                  data and is retained independently of any individual workspace.
                </p>

                <h2 id="signatures">13. Signatures &amp; contact</h2>
                <p>
                  This DPA is accepted by the customer when it accepts the Revailo Sales Terms of
                  Service or otherwise begins using the service, and it is countersigned on behalf of
                  Revailo Sales by its authorized representative. No physical signature is required
                  for this DPA to be binding.
                </p>
                <p>
                  Questions about this DPA, or a request for a signed copy or the current
                  subprocessor list, can be directed to our data protection team:
                </p>
                <ul>
                  <li>
                    <strong>Revailo Sales &mdash; Data Protection</strong>
                  </li>
                  <li>Email: privacy@revailo-sales.com</li>
                  <li>
                    Web:{' '}
                    <a href="https://revailo-sales.com/dpa">revailo-sales.com/dpa</a>
                  </li>
                </ul>

                <p style={{ marginTop: 28 }}>
                  For how we handle personal data generally, see our{' '}
                  <Link to="/privacy">Privacy Notice</Link>, or{' '}
                  <Link to="/contact">contact us</Link> with any question.
                </p>
              </Prose>
            </LegalLayout>
          </Reveal>
        </Container>
      </Section>
    </MarketingShell>
  )
}
